/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { SecurityHelper } from 'sarsaparilla';
import axios from 'axios';

const BuyNowContext = createContext({
    showBuyNow: false,
    canBuyNow: false,
    reservationId: undefined,
    locationId: undefined,
    setShowBuyNow: () => {},
    setReservationId: () => {},
    setLocationId: () => {},
});

export function BuyNowProvider({ children }) {
    const [showBuyNow, setShowBuyNow] = useState(false);
    const [canBuyNow, setCanBuyNow] = useState(false);
    const [reservationId, setReservationId] = useState(undefined);
    // Host-supplied current location (facility id). Used by the Buy Now button to
    // apply location exclusion when the cart has no reservation yet.
    const [locationId, setLocationId] = useState(undefined);

    useEffect(() => {
        if (!showBuyNow) return;
        axios
            .get(`${process.env.API}/cart/buy-now`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => setCanBuyNow(response.data.can_buy_now))
            .catch(() => setCanBuyNow(false));
    }, [showBuyNow]);

    const value = useMemo(
        () => ({
            showBuyNow,
            canBuyNow,
            reservationId,
            locationId,
            setShowBuyNow,
            setReservationId,
            setLocationId,
        }),
        [showBuyNow, canBuyNow, reservationId, locationId]
    );

    return <BuyNowContext.Provider value={value}>{children}</BuyNowContext.Provider>;
}

export function useBuyNowContext() {
    return useContext(BuyNowContext);
}

export function useBuyNowRegister(showBuyNow, reservationId, locationId) {
    const { setShowBuyNow, setReservationId, setLocationId } = useContext(BuyNowContext);
    useEffect(() => {
        setShowBuyNow(showBuyNow);
        setReservationId(reservationId);
        setLocationId(locationId);
        return () => {
            setShowBuyNow(false);
            setReservationId(undefined);
            setLocationId(undefined);
        };
    }, [
        showBuyNow,
        reservationId,
        locationId,
        setShowBuyNow,
        setReservationId,
        setLocationId,
    ]);
}
